import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

export default function OrdersPage() {
  const { user, loading } = useAuth(); // ✅ This is auth loading
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
    if (!loading && !user) {
        navigate('/login');
    }
    }, [user, loading, navigate]);

  const statusFilters = {
    all: 'All Orders',
    pending: '🔄 Pending',
    processing: '⚡ Processing',
    shipped: '🚚 Shipped',
    delivered: '✅ Delivered',
    cancelled: '❌ Cancelled'
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const { data } = await api.get('/orders', {
          params: {
            status: activeTab === 'all' ? undefined : activeTab
          }
        });
        setOrders(data.data.orders);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
        toast.error('Failed to load your orders');
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [user, navigate, activeTab]);

  const handleViewOrderDetails = async (orderId) => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setSelectedOrder(data.data.order);
    } catch (err) {
      console.error('Error fetching order details:', err);
      toast.error('Failed to load order details');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-purple-100 text-purple-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-yellow-100 text-yellow-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '🔄';
      case 'processing': return '⚡';
      case 'shipped': return '🚚';
      case 'delivered': return '✅';
      case 'cancelled': return '❌';
      default: return '📦';
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const closeModal = () => {
    setSelectedOrder(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-indigo-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center items-center h-96">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-12 w-12 bg-pink-300 rounded-full mb-4"></div>
              <div className="h-4 w-32 bg-pink-200 rounded mb-2"></div>
              <div className="h-4 w-64 bg-pink-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-indigo-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-6 text-center">
            <div className="text-5xl mb-4">😢</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-pink-500 text-white rounded-full hover:bg-pink-600 transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-indigo-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Your Orders</h1>
            <p className="text-gray-600">Track and manage all your purchases</p>
          </div>
          <div className="mt-4 md:mt-0">
            <button 
              onClick={() => navigate('/stores')}
              className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-all flex items-center"
            >
              <span className="mr-2">🛍️</span> Continue Shopping
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            {Object.entries(statusFilters).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${activeTab === key ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="text-6xl mb-6">📭</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No orders found</h3>
            <p className="text-gray-600 mb-6">
              {activeTab === 'all' 
                ? "You haven't placed any orders yet." 
                : `You don't have any ${activeTab} orders.`}
            </p>
            <button 
              onClick={() => navigate('/shop')}
              className="px-6 py-2 bg-pink-500 text-white rounded-full hover:bg-pink-600 transition-all"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div 
                key={order._id} 
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all"
              >
                <div className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div className="mb-4 md:mb-0">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">{getStatusIcon(order.status)}</span>
                        <h3 className="text-lg font-bold text-gray-900">Order #{order.orderNumber}</h3>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">Placed on {formatDate(order.createdAt)}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                      <span className="text-lg font-bold">₹{order.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex overflow-x-auto pb-2 -mx-2">
                      {order.items.slice(0, 4).map((item) => (
                        <div key={item._id} className="flex-shrink-0 px-2">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                            {item.product?.image ? (
                              <img 
                                src={item.product.image} 
                                alt={item.product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                🛍️
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {order.items.length > 4 && (
                        <div className="flex-shrink-0 px-2">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <span className="text-gray-500">+{order.items.length - 4}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => handleViewOrderDetails(order._id)}
                      className="px-4 py-2 border border-black text-black rounded-full hover:bg-gray-50 transition-all text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Order #{selectedOrder.orderNumber}</h3>
                    <p className="text-sm text-gray-500">Placed on {formatDate(selectedOrder.createdAt)}</p>
                  </div>
                  <button 
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                {/* Order Status Timeline */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Order Status</h4>
                  <div className="relative">
                    <div className="absolute left-4 top-0 h-full w-0.5 bg-gray-200"></div>
                    {['pending', 'processing', 'shipped', 'delivered'].map((status, index) => {
                      const isActive = selectedOrder.status === status;
                      const isCompleted = 
                        status === 'pending' ? true :
                        status === 'processing' ? ['processing', 'shipped', 'delivered'].includes(selectedOrder.status) :
                        status === 'shipped' ? ['shipped', 'delivered'].includes(selectedOrder.status) :
                        status === 'delivered' ? selectedOrder.status === 'delivered' : false;
                      
                      return (
                        <div key={status} className="relative pl-10 pb-6">
                          <div className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                            {isCompleted ? '✓' : index + 1}
                          </div>
                          <div className="min-h-8">
                            <p className={`font-medium ${isActive ? 'text-green-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'}`}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </p>
                            {isActive && selectedOrder.tracking?.number && (
                              <p className="text-sm text-gray-500 mt-1">
                                Tracking #: {selectedOrder.tracking.number}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Items</h4>
                  <div className="space-y-4">
                    {selectedOrder.items.map((item) => (
                      <div key={item._id} className="flex border-b border-gray-100 pb-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {item.product?.image ? (
                            <img 
                              src={item.product.image} 
                              alt={item.product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              🛍️
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex-1">
                          <h5 className="font-medium text-gray-900">{item.product?.name || 'Product'}</h5>
                          <p className="text-sm text-gray-500">
                            {item.variant?.color?.name && `${item.variant.color.name}, `}
                            {item.variant?.size && `Size ${item.variant.size}, `}
                            Qty: {item.quantity}
                          </p>
                          <p className="text-sm text-gray-500">Print: {item.printMethod}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Order Summary</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span>₹{selectedOrder.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping</span>
                        <span>₹{selectedOrder.shipping.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax</span>
                        <span>₹{selectedOrder.tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200 mt-2">
                        <span>Total</span>
                        <span>₹{selectedOrder.totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Shipping Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium">
                      {selectedOrder.shippingAddress.firstName} {selectedOrder.shippingAddress.lastName}
                    </p>
                    <p className="text-gray-600">{selectedOrder.shippingAddress.street}</p>
                    <p className="text-gray-600">
                      {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.pincode}
                    </p>
                    <p className="text-gray-600">{selectedOrder.shippingAddress.country}</p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}